import { AnalyticsProvider } from "@/Contexts/AnalyticsProvider";
import { CookieManagementProvider } from "@/Contexts/CookieManagementProvider";
import { ModalManagerProvider } from "@/Contexts/ModalManagerProvider";
import { listenToAppEvent, sendEventToApp } from "@/utils/appCommunication";
import { MobileAppTracking } from "@/utils/MobileAppTracking";
import { PropsWithChildren, useEffect } from "react";

/**
 * The providers every page needs, logged in or not. The logged-in providers
 * live in GlobalStateProviders instead, which AppRouter loads only for logged
 * in visitors: ChatProvider pulls in stream-chat-react and the payment and
 * scheduling providers pull in cronofy-elements, roughly 3MB of JavaScript a
 * logged-out marketing page would otherwise download before its first paint.
 *
 * Components shared with logged-out pages may still call useContext on those
 * logged-in providers. Each of those contexts has a default value, and the UI
 * the defaults hide never renders for logged-out visitors.
 */
export function RequiredProviders({ children }: PropsWithChildren) {
    useEffect(() => {
        sendEventToApp("appInitialized", {});
        const unsubscribe = listenToAppEvent("webViewAliveCheck", () => {
            sendEventToApp("isAlive", true);
        });
        return () => unsubscribe();
    }, []);

    return (
        <ModalManagerProvider>
            <CookieManagementProvider>
                <AnalyticsProvider>
                    <MobileAppTracking>{children}</MobileAppTracking>
                </AnalyticsProvider>
            </CookieManagementProvider>
        </ModalManagerProvider>
    );
}
